import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const prefix = isEnglish ? '/en' : '';

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/home/background_birds.jpg')" }}
        >
          <div className="absolute inset-0 bg-primary-900 bg-opacity-60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-8" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  {t('hero.title').split('Confidenza')[0]}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-purple-300">
                    Confidenza {isEnglish ? 'Insurance' : 'Seguros'}
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-primary-100 leading-relaxed">
                  {t('hero.subtitle')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={isEnglish ? `${prefix}/contact` : `${prefix}/contacto`}
                  className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition-all duration-300 transform hover:scale-105 shadow-lg text-center"
                >
                  {t('hero.cta')}
                </Link>
                <Link
                  to={isEnglish ? `${prefix}/services` : `${prefix}/servicios`}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-700 transition-all duration-300 text-center"
                >
                  {t('hero.secondaryCta')}
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 pt-8">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-primary-100">{t('hero.trust.experience')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-primary-100">{t('hero.trust.certified')}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src="/images/mascota/OUSI_Mesa de trabajo 1.png"
                  alt="OUSI Mascot"
                  className="w-72 md:w-80 lg:w-96 h-auto drop-shadow-2xl"
                />
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-pink-300 rounded-full opacity-80 animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-purple-300 rounded-full opacity-80 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Trust Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary-600">15+</div>
              <div className="text-gray-600 mt-2">{isEnglish ? 'Years of Experience' : 'Años de Experiencia'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary-600">500+</div>
              <div className="text-gray-600 mt-2">{isEnglish ? 'Protected Clients' : 'Clientes Protegidos'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary-600">8</div>
              <div className="text-gray-600 mt-2">{isEnglish ? 'Insurance Lines' : 'Líneas de Seguros'}</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary-600">MDRT</div>
              <div className="text-gray-600 mt-2">{isEnglish ? 'Certified Team' : 'Equipo Certificado'}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Major Medical */}
            <Link
              to={isEnglish ? `${prefix}/services/major-medical` : `${prefix}/servicios/gastos-medicos-mayores`}
              className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="h-32 bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600">{t('services.majorMedical.title')}</h3>
                <p className="text-gray-600 text-sm">{t('services.majorMedical.shortDesc')}</p>
              </div>
            </Link>

            {/* Life Insurance */}
            <Link
              to={isEnglish ? `${prefix}/services/life-insurance` : `${prefix}/servicios/seguro-de-vida`}
              className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="h-32 bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600">{t('services.life.title')}</h3>
                <p className="text-gray-600 text-sm">{t('services.life.shortDesc')}</p>
              </div>
            </Link>

            {/* Wealth Architecture */}
            <Link
              to={isEnglish ? `${prefix}/services/wealth-architecture` : `${prefix}/servicios/arquitectura-patrimonial`}
              className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="h-32 bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600">{t('services.wealth.title')}</h3>
                <p className="text-gray-600 text-sm">{t('services.wealth.shortDesc')}</p>
              </div>
            </Link>

            {/* Bonds */}
            <Link
              to={isEnglish ? `${prefix}/services/bonds` : `${prefix}/servicios/fianzas`}
              className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="h-32 bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600">{t('services.bonds.title')}</h3>
                <p className="text-gray-600 text-sm">{t('services.bonds.shortDesc')}</p>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to={isEnglish ? `${prefix}/services` : `${prefix}/servicios`}
              className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
            >
              {t('services.viewAll')}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t('features.items', { returnObjects: true }) as Array<{ title: string; description: string }>).map((feature, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            {t('cta.subtitle')}
          </p>
          <Link
            to={isEnglish ? `${prefix}/contact` : `${prefix}/contacto`}
            className="inline-block bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
